import { isValidProductId } from './payment-link';

/**
 * The two ends of the link in a review-request email
 * (functions/src/lib/review-requests.ts, owner decision 2026-10-18).
 *
 * An ACCOUNT purchase's stars go to the product page with `?action=review&
 * rating=N`, which opens the review dialog on that rating. The email is often
 * opened on a phone that is not signed in, so a signed-out visitor with no guest
 * order in the tab is sent to sign in and brought back to the same link.
 *
 * A GUEST purchase's stars go to /review with a signed link that may review that
 * one product on that one order (and nothing else: it grants no download). The
 * page reads the link's order and product to show what is being reviewed; the
 * server checks the signature when the review is posted (submitGuestReview).
 */

export type ReviewLinkStep =
  | { kind: 'wait' }
  | { kind: 'signIn' }
  | { kind: 'open'; initialRating: number | null };

/** The rating a link asks for: a whole number from 1 to 5, or null. */
function ratingFrom(param: string | null): number | null {
  if (param === null || !/^[1-5]$/.test(param)) return null;
  return Number(param);
}

/** What the product page does with `?action=review`. */
export function reviewLinkStep(input: {
  /** Whether sign-in has finished restoring, either way. */
  authSettled: boolean;
  signedIn: boolean;
  /** This tab holds a guest order for the product (checkout or /access put it there). */
  guestOrderInTab: boolean;
  ratingParam: string | null;
}): ReviewLinkStep {
  if (!input.authSettled) return { kind: 'wait' };
  if (!input.signedIn && !input.guestOrderInTab) return { kind: 'signIn' };
  return { kind: 'open', initialRating: ratingFrom(input.ratingParam) };
}

export type ReviewInvite = { orderId: string; productId: string; expiresAtMs: number };

/** A payment intent id as a guest order carries it. */
const ORDER_ID_PATTERN = /^[A-Za-z0-9_-]{1,200}$/;

function base64UrlToText(value: string): string {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * The order and product a guest's review link names, or null when it is not a
 * review link or has expired. Only READS the link, to show the right product:
 * the signature is the server's to check, so nothing here is trusted beyond what
 * the page displays.
 */
export function decodeReviewInvite(token: string, nowMs: number): ReviewInvite | null {
  const parts = token.split('.');
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  let payload: unknown;
  try {
    payload = JSON.parse(base64UrlToText(parts[0]));
  } catch {
    return null;
  }
  if (!payload || typeof payload !== 'object') return null;
  const value = payload as Record<string, unknown>;
  if (value.type !== 'review_invite') return null;
  if (typeof value.piId !== 'string' || !ORDER_ID_PATTERN.test(value.piId)) return null;
  if (!isValidProductId(value.productId)) return null;
  if (typeof value.exp !== 'number' || !Number.isFinite(value.exp) || value.exp < nowMs) return null;
  return { orderId: value.piId, productId: value.productId, expiresAtMs: value.exp };
}
